import { useId, useState } from 'react';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Dialog } from '../../ui/Dialog';
import { Pill } from '../../ui/DataBits';
import { ClassesData, errorText, NATIONAL_LEVELS, RoleSpec, rpc, SchoolRole } from './api';

/** Copies text; says so in the live region (no toast that vanishes). */
export function CopyButton({ text, label }: { text: string; label: string }) {
  const { t } = useI18n();
  const announce = useAnnounce();
  const [done, setDone] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      announce(t['pa.copied']);
      window.setTimeout(() => setDone(false), 2000);
    } catch {
      announce(t['pa.copyFailed']);
    }
  }
  return (
    <button type="button" className="link-btn copy-btn" onClick={copy} aria-label={label}>
      {done ? t['pa.copied'] : t['pa.copy']}
    </button>
  );
}

/** Grade levels as grouped checkboxes (national list). */
export function LevelPicker({
  value,
  onChange,
  legend,
  hint,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  legend: string;
  hint?: string;
}) {
  const { t } = useI18n();
  const id = useId();
  const groups: [string, readonly string[]][] = [
    [t['pa.levelsBasic'], NATIONAL_LEVELS.slice(0, 9)],
    [t['pa.levelsUpper'], NATIONAL_LEVELS.slice(9, 12)],
    [t['pa.levelsVocational'], NATIONAL_LEVELS.slice(12)],
  ];
  const toggle = (l: string) => onChange(value.includes(l) ? value.filter((x) => x !== l) : [...value, l]);
  return (
    <fieldset className="level-picker" aria-describedby={hint ? `${id}-hint` : undefined}>
      <legend className="field-label">{legend}</legend>
      {groups.map(([g, levels]) => (
        <div key={g} className="level-group" role="group" aria-label={g}>
          <span className="level-group-name" aria-hidden="true">
            {g}
          </span>
          <span className="level-boxes">
            {levels.map((l) => (
              <label key={l} className={`chip-check${value.includes(l) ? ' on' : ''}`}>
                <input type="checkbox" checked={value.includes(l)} onChange={() => toggle(l)} />
                {t[`lvl.${l as (typeof NATIONAL_LEVELS)[number]}`]}
              </label>
            ))}
          </span>
        </div>
      ))}
      {hint && (
        <p className="form-hint" id={`${id}-hint`}>
          {hint}
        </p>
      )}
    </fieldset>
  );
}

export const SUPPORT_REASONS = [
  'school_asked',
  'setup_stuck',
  'account_recovery',
  'approval_no_primary_head',
  'test_accounts',
  'other',
] as const;

/** G1 — Enter support mode: a reason (picker) and, for "Other", a note. */
export function EnterSupportDialog({
  open,
  schoolId,
  schoolName,
  onClose,
  onEntered,
  defaultReason = 'school_asked',
}: {
  open: boolean;
  schoolId: string;
  schoolName: string;
  onClose: () => void;
  onEntered: () => void;
  defaultReason?: (typeof SUPPORT_REASONS)[number];
}) {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [reason, setReason] = useState<string>(defaultReason);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    const r = await rpc('platform_enter_support', { p_school_id: schoolId, p_reason: reason, p_note: note || null });
    setBusy(false);
    if (r.error) return setError(errorText(t, r.error));
    announce(t['pa.supportEntered'](schoolName));
    onEntered();
  }

  return (
    <Dialog
      open={open}
      title={t['pa.enterSupport']}
      intro={<p className="form-hint">{t['pa.enterSupportIntro']}</p>}
      submitLabel={t['pa.enterSupportGo']}
      busy={busy}
      error={error}
      onSubmit={submit}
      onClose={onClose}
    >
      <div className="field-group">
        <label className="field-label" htmlFor={`${id}-r`}>
          {t['pa.reason']}
        </label>
        <span className="select-wrap">
          <select id={`${id}-r`} className="select" value={reason} onChange={(e) => setReason(e.target.value)}>
            {SUPPORT_REASONS.map((r) => (
              <option key={r} value={r}>
                {t[`pa.sr_${r}`]}
              </option>
            ))}
          </select>
        </span>
      </div>
      <div className="field-group">
        <label className="field-label" htmlFor={`${id}-n`}>
          {reason === 'other' ? t['pa.note'] : t['pa.noteOptional']}
        </label>
        <input id={`${id}-n`} className="field" value={note} onChange={(e) => setNote(e.target.value)} autoComplete="off" />
      </div>
    </Dialog>
  );
}

const ROLES: SchoolRole[] = ['head_of_school', 'school_admin', 'counsellor', 'teacher'];

/**
 * C1/C5 role pickers: checkboxes with one-line descriptions, and the scope
 * for each ticked role (classes for Teacher, grades for Counsellor).
 */
export function RolePicker({
  value,
  onChange,
  classes,
  exclude = [],
  single = false,
}: {
  value: RoleSpec[];
  onChange: (v: RoleSpec[]) => void;
  classes: ClassesData | null;
  exclude?: SchoolRole[];
  single?: boolean;
}) {
  const { t } = useI18n();
  const id = useId();
  const has = (r: SchoolRole) => value.find((v) => v.role === r);
  const set = (r: SchoolRole, patch: Partial<RoleSpec> | null) => {
    if (patch === null) return onChange(value.filter((v) => v.role !== r));
    const cur = has(r);
    if (single) return onChange([{ role: r, ...(cur ?? {}), ...patch }]);
    onChange(cur ? value.map((v) => (v.role === r ? { ...v, ...patch } : v)) : [...value, { role: r, ...patch }]);
  };
  const desc: Record<SchoolRole, string> = {
    head_of_school: t['role.headOfSchoolDesc'],
    school_admin: t['role.schoolAdminDesc'],
    counsellor: t['role.counsellorDesc'],
    teacher: t['role.teacherDesc'],
  };
  const name: Record<SchoolRole, string> = {
    head_of_school: t['role.headOfSchool'],
    school_admin: t['role.schoolAdmin'],
    counsellor: t['role.counsellor'],
    teacher: t['role.teacher'],
  };
  const activeClasses = classes?.classes.filter((c) => c.status === 'active') ?? [];

  return (
    <fieldset className="choice-list">
      <legend className="field-label">{single ? t['pa.role'] : t['pa.roles']}</legend>
      {ROLES.filter((r) => !exclude.includes(r)).map((r) => {
        const cur = has(r);
        return (
          <div key={r} className={`choice${cur ? ' on' : ''}`}>
            <label className="choice-head">
              <input
                type={single ? 'radio' : 'checkbox'}
                name={`${id}-role`}
                checked={!!cur}
                onChange={(e) => set(r, e.target.checked ? {} : null)}
              />
              <span>
                <strong>{name[r]}</strong>
                <span className="choice-desc">{desc[r]}</span>
              </span>
            </label>
            {cur && r === 'head_of_school' && (
              <label className="check sub">
                <input type="checkbox" checked={!!cur.is_primary} onChange={(e) => set(r, { is_primary: e.target.checked })} />{' '}
                {t['pa.makePrimary']}
              </label>
            )}
            {cur && r === 'counsellor' && classes && (
              <div className="sub">
                <fieldset className="inline-radios">
                  <legend className="field-label">{t['pa.counsellorScope']}</legend>
                  <label>
                    <input type="radio" name={`${id}-cs`} checked={!cur.scope_grade_ids} onChange={() => set(r, { scope_grade_ids: null })} />{' '}
                    {t['pa.wholeSchool']}
                  </label>
                  <label>
                    <input type="radio" name={`${id}-cs`} checked={!!cur.scope_grade_ids} onChange={() => set(r, { scope_grade_ids: [] })} />{' '}
                    {t['pa.chosenGrades']}
                  </label>
                </fieldset>
                {cur.scope_grade_ids && (
                  <span className="level-boxes">
                    {classes.grades.map((g) => (
                      <label key={g.id} className={`chip-check${cur.scope_grade_ids?.includes(g.id) ? ' on' : ''}`}>
                        <input
                          type="checkbox"
                          checked={!!cur.scope_grade_ids?.includes(g.id)}
                          onChange={(e) =>
                            set(r, {
                              scope_grade_ids: e.target.checked
                                ? [...(cur.scope_grade_ids ?? []), g.id]
                                : (cur.scope_grade_ids ?? []).filter((x) => x !== g.id),
                            })
                          }
                        />
                        {t['pa.gradeN'](g.label)}
                      </label>
                    ))}
                  </span>
                )}
              </div>
            )}
            {cur && r === 'teacher' && (
              <div className="sub">
                {activeClasses.length === 0 ? (
                  <p className="form-hint">{t['pa.noClassesYet']}</p>
                ) : (
                  <fieldset>
                    <legend className="field-label">{t['pa.classesOptional']}</legend>
                    <ul className="class-picks">
                      {activeClasses.map((c) => {
                        const pick = cur.classes?.find((x) => x.class_id === c.id);
                        return (
                          <li key={c.id}>
                            <label className="chip-check-row">
                              <input
                                type="checkbox"
                                checked={!!pick}
                                onChange={(e) =>
                                  set(r, {
                                    classes: e.target.checked
                                      ? [...(cur.classes ?? []), { class_id: c.id, kind: c.teachers.some((x) => x.kind === 'homeroom') ? 'subject' : 'homeroom' }]
                                      : (cur.classes ?? []).filter((x) => x.class_id !== c.id),
                                  })
                                }
                              />{' '}
                              {c.label}
                            </label>
                            {pick && (
                              <span className="select-wrap select-sm">
                                <select
                                  className="select"
                                  aria-label={t['pa.kindFor'](c.label)}
                                  value={pick.kind}
                                  onChange={(e) =>
                                    set(r, {
                                      classes: (cur.classes ?? []).map((x) =>
                                        x.class_id === c.id ? { ...x, kind: e.target.value as 'homeroom' | 'subject' } : x,
                                      ),
                                    })
                                  }
                                >
                                  <option value="homeroom">{t['pa.kind_homeroom']}</option>
                                  <option value="subject">{t['pa.kind_subject']}</option>
                                </select>
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </fieldset>
                )}
              </div>
            )}
          </div>
        );
      })}
    </fieldset>
  );
}

export type Tier = 'tier_a' | 'tier_b';

/**
 * Tier B (a dedicated database per school) isn't offered yet: shown, greyed
 * out, not selectable (decision 2026-10-11). The database refuses it too
 * (0014). Flip this together with platform_settings.tier_b_enabled once
 * dedicated databases can be provisioned and run from this platform.
 */
export const TIER_B_AVAILABLE = false;

/**
 * Tier A / Tier B for a Customer school, each with what it means (hosting
 * and price). Used by the add-school wizard, School settings and the
 * Sandbox → Customer dialog.
 */
export function TierPicker({ value, onChange }: { value: string; onChange: (v: Tier) => void }) {
  const { t } = useI18n();
  const name = useId();
  return (
    <fieldset className="choice-list">
      <legend className="field-label">{t['pa.tier']}</legend>
      {(['tier_a', 'tier_b'] as const).map((v) => {
        // A school already on Tier B keeps it selectable (and selected).
        const off = v === 'tier_b' && !TIER_B_AVAILABLE && value !== 'tier_b';
        return (
          <label key={v} className={`choice${value === v ? ' on' : ''}${off ? ' is-disabled' : ''}`}>
            <input type="radio" name={name} value={v} checked={value === v} disabled={off} onChange={() => onChange(v)} />
            <span>
              <strong>{t[`pa.${v}`]}</strong>
              {off && (
                <>
                  {' '}
                  <Pill kind="neutral">{t['pa.comingLater']}</Pill>
                </>
              )}
              <span className="choice-desc">{t[`pa.${v}Desc`]}</span>
              {off && <span className="choice-desc">{t['pa.tier_bSoon']}</span>}
            </span>
          </label>
        );
      })}
      {TIER_B_AVAILABLE && <p className="form-hint">{t['pa.tierHint']}</p>}
    </fieldset>
  );
}
